/**
 * Which icon each thing wears, by convention.
 *
 * The UI never keeps a table of content-id -> icon-id. Every icon id is derived
 * from the content id with a fixed prefix, and `icon-map.ts` (generated by
 * tools/art/icons.mjs) is the single mapping from icon id to sheet cell. The
 * sheet is drawn with exactly the ids `requiredIconIds()` lists, so adding a
 * tool, upgrade, card, Training node, pickup or achievement to content.ts is
 * the whole job: its icon id already exists.
 *
 *   tool_<ToolId>        shop rows, hotkey hints
 *   upg_<UpgradeId>      upgrade rows
 *   card_<CardId>        draft cards, the summary picker, the card strip
 *   meta_<MetaUpgradeId> Training nodes
 *   pickup_<id>          the collect toast
 *   AchievementDef.icon  the achievements screen and the unlock popup
 *   ui_*                 HUD chrome (below)
 */
import {
  ACHIEVEMENTS,
  CARDS,
  META_UPGRADES,
  PICKUPS,
  TOOLS,
  UPGRADES,
} from '../sim/content.ts';

export const toolIcon = (id: string): string => `tool_${id}`;
export const upgradeIcon = (id: string): string => `upg_${id}`;
export const cardIcon = (id: string): string => `card_${id}`;
export const metaIcon = (id: string): string => `meta_${id}`;
export const pickupIcon = (id: string): string => `pickup_${id}`;

/** Chrome icons, each with the text it stands in for when the sheet lacks it. */
export const UI_ICONS = {
  /** The 👍 currency: the HUD tally, Training's balance, the run-over total. */
  thumbs: 'ui_thumbs',
  /** The CONTEXT WINDOW bar label. */
  context: 'ui_context',
  /** The HUMAN PATIENCE bar label. */
  patience: 'ui_patience',
  /** The /compact button. */
  compact: 'ui_compact',
  /** The report button while it reads CLAIM DONE. */
  claim: 'ui_claim',
} as const;

/** Every icon id the UI will ask the sheet for, deduplicated, in a stable order. */
export function requiredIconIds(): string[] {
  const out = new Set<string>();
  for (const t of TOOLS) out.add(toolIcon(t.id));
  for (const u of UPGRADES) out.add(upgradeIcon(u.id));
  for (const c of CARDS) out.add(cardIcon(c.id));
  for (const m of META_UPGRADES) out.add(metaIcon(m.id));
  for (const p of PICKUPS) out.add(pickupIcon(p.id));
  for (const a of ACHIEVEMENTS) out.add(a.icon);
  for (const id of Object.values(UI_ICONS)) out.add(id);
  return [...out];
}
